import { Type } from "@google/genai";
import { getGenerativeModel } from "firebase/ai";
import { ai } from "../../../../firebase/config";
import { getModelForFeature } from "../../../../../services/aiConfigService";
import { ExamPaper } from "../components/types";
import { DocumentStyleAnalysis } from "./documentService";

type GenerateExamPaperParams = {
    subject: string;
    topics: string[];
    difficulty: string;
    instructions: string;
    isLocalised?: boolean;
    includeDiagrams?: boolean;
    diagramCount?: string;   // 'random' | '1' | '2' | '3' | '4'
    diagramTopics?: string;  // optional comma-separated topics to target
    documentStyleAnalysis?: DocumentStyleAnalysis;
    sections: {
        name: string;
        questionType: string;
        questionStyle: string;
        numQuestions: number;
        marks: number;
        enableOptions?: boolean;
        numOptions?: number;
    }[];
    styleProfileSnapshot?: string;
};

// ─── JSON Cleaning & Parsing ──────────────────────────────────────────────────

/** Patterns that indicate the AI is "thinking out loud" inside a JSON string */
const AI_THINKING_PATTERNS = [
    /\blet me (re-?check|re-?read|re-?verify|re-?calculate|correct|review|start|try|re-?do)\b/i,
    /\bI (will|need to|must|made|think|realize|see|am getting|get)\b/i,
    /\bActually[,.]?/i,
    /\bWait[,.]?/i,
    /\bHmm[,.]?/i,
    /\bOops[,.]?/i,
    /\bthe prompt (says|asks|requires|states|tells)/i,
    /re-reading the (prompt|question|problem)/i,
    /\bI made an (error|mistake)/i,
    /\blet me re-?start\b/i,
    /\bLet me check\b/i,
    /\b(my previous|previous) (calculation|answer) was/i,
];

const sanitizeExplanationStep = (step: unknown): string | null => {
    if (typeof step !== 'string') return null;
    const s = step.trim();
    if (!s) return null;
    // Truncate aggressively — real exam steps should never exceed ~350 chars
    const truncated = s.length > 350 ? s.slice(0, 350) + '...' : s;
    // If the entire step is AI thinking, drop it
    for (const pattern of AI_THINKING_PATTERNS) {
        if (pattern.test(truncated)) return null;
    }
    // Trim at the first occurrence of thinking
    let clean = truncated;
    for (const pattern of AI_THINKING_PATTERNS) {
        const match = pattern.exec(clean);
        if (match && match.index > 20) {
            // Only trim if there's real content before the thinking
            clean = clean.slice(0, match.index).trim();
        }
    }
    return clean.length > 5 ? clean : null;
};

const cleanJsonCandidate = (raw: string) => {
    // 1. Strip markdown code fences
    let s = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
    // 2. Extract from first { to last }
    const first = s.indexOf('{');
    const last = s.lastIndexOf('}');
    if (first >= 0 && last > first) s = s.slice(first, last + 1);
    return s;
};

const parseExamPaperJson = (raw: string): ExamPaper => {
    const candidate = cleanJsonCandidate(raw);
    const parsed = JSON.parse(candidate) as ExamPaper;
    if (!parsed?.sections || !Array.isArray(parsed.sections)) {
        throw new Error('Parsed response did not contain sections.');
    }
    return parsed;
};

const getMarksForQuestion = (sectionMarks: number, count: number, index: number) => {
    const base = Math.floor(sectionMarks / Math.max(count, 1));
    const remainder = sectionMarks - base * Math.max(count, 1);
    return base + (index < remainder ? 1 : 0);
};

const createFallbackExamPaper = (params: GenerateExamPaperParams): ExamPaper => ({
    title: `${params.subject || 'Assessment'} Assessment`,
    sections: params.sections.map((section) => ({
        name: section.name,
        instructions: 'Answer all questions. Show all necessary working.',
        questions: Array.from({ length: section.numQuestions }, (_, index) => {
            const marks = getMarksForQuestion(section.marks, section.numQuestions, index);
            const topic = params.topics[index % Math.max(params.topics.length, 1)] || params.subject || 'the topic';
            const shouldUseDiagram = Boolean(params.includeDiagrams && index === 0);
            const diagramMarker = shouldUseDiagram
                ? '\n%%DIAGRAM%%{"type":"area-region","equation":"y = x^2","xRange":[0,4],"yRange":[0,18],"shadeRange":{"from":1,"to":3},"verticalLines":[1,3],"caption":"Region R under y = x^2 from x = 1 to x = 3"}%%'
                : '';
            return {
                id: `Q${index + 1}`,
                text: shouldUseDiagram
                    ? `Study the graph below showing region R bounded by $y = x^2$, the x-axis, $x = 1$, and $x = 3$. Set up and evaluate the definite integral for the area of R.${diagramMarker}`
                    : `Solve a ${params.difficulty.toLowerCase()} ${params.subject} problem on ${topic}. Show all necessary working.`,
                type: section.questionType === 'multiple-choice' ? 'multiple-choice' : 'short-answer',
                marks,
                difficulty: (params.difficulty === 'Easy' || params.difficulty === 'Hard' ? params.difficulty : 'Medium') as 'Easy' | 'Medium' | 'Hard',
                topic,
                options: section.questionType === 'multiple-choice' ? ['A', 'B', 'C', 'D'] : undefined,
                answer: shouldUseDiagram ? '$$\\int_1^3 x^2 dx = \\frac{26}{3}$$' : 'See marking scheme.',
                explanation: shouldUseDiagram
                    ? [
                        'The required area is the area under $y=x^2$ from $x=1$ to $x=3$.',
                        '$$\\int_1^3 x^2 dx = \\left[\\frac{x^3}{3}\\right]_1^3 = \\frac{27-1}{3} = \\frac{26}{3}$$'
                    ]
                    : [
                        `Use the standard method for ${topic}.`,
                        'Award marks for correct setup, working, and final answer.'
                    ],
            };
        }),
    })),
});

const normalizeGeneratedPaper = (paper: ExamPaper, params: GenerateExamPaperParams): ExamPaper => ({
    title: paper.title || `${params.subject || 'Assessment'} Assessment`,
    sections: (paper.sections || []).map((section, sectionIndex) => {
        const requested = params.sections[sectionIndex] || params.sections[0];
        const questions = Array.isArray(section.questions) ? section.questions : [];
        return {
            name: section.name || requested?.name || `Section ${String.fromCharCode(65 + sectionIndex)}`,
            instructions: section.instructions || 'Answer all questions. Show all necessary working.',
            questions: questions.map((question: any, questionIndex) => {
                const cleanExplanation = (arr: unknown[]) =>
                    arr.slice(0, 5)
                        .map(sanitizeExplanationStep)
                        .filter(Boolean) as string[];

                const rawExplanation = Array.isArray(question.explanation) && question.explanation.length > 0
                    ? cleanExplanation(question.explanation)
                    : [];
                const explanation = rawExplanation.length > 0
                    ? rawExplanation
                    : ['Award marks for correct setup, working, and final answer.'];

                // Support diagram coming via the 'diagram' field (structured JSON)
                // or embedded in text as %%DIAGRAM%%...%% (legacy inline)
                let diagramSpec = question.diagram || null;
                let questionText = String(question.text || `Answer this ${params.subject} question.`);

                // If no diagram field but diagram marker is in text, extract it
                if (!diagramSpec && questionText.includes('%%DIAGRAM%%')) {
                    const markerMatch = questionText.match(/%%DIAGRAM%%(.*?)%%/s);
                    if (markerMatch) {
                        try { diagramSpec = JSON.parse(markerMatch[1]); } catch { /* ignore */ }
                        // Remove the raw marker from text so MarkdownText doesn't double-render
                        questionText = questionText.replace(/%%DIAGRAM%%.*?%%/s, '').trim();
                    }
                }

                return {
                    ...question,
                    id: question.id || `Q${questionIndex + 1}`,
                    text: questionText,
                    type: question.type || 'short-answer',
                    marks: Number(question.marks) || getMarksForQuestion(requested?.marks || 0, requested?.numQuestions || questions.length || 1, questionIndex),
                    difficulty: question.difficulty || 'Medium',
                    answer: question.answer || 'See marking scheme.',
                    explanation,
                    diagram: diagramSpec,
                    subQuestions: question.subQuestions?.map((subQuestion: any, subIndex: number) => {
                        const rawSubExplanation = Array.isArray(subQuestion.explanation) && subQuestion.explanation.length > 0
                            ? subQuestion.explanation.slice(0, 4).map(sanitizeExplanationStep).filter(Boolean) as string[]
                            : [];
                        const subExplanation = rawSubExplanation.length > 0
                            ? rawSubExplanation
                            : ['Award marks for correct working.'];

                        let subDiagram = subQuestion.diagram || null;
                        let subText = String(subQuestion.text || 'Answer this part.');
                        if (!subDiagram && subText.includes('%%DIAGRAM%%')) {
                            const m = subText.match(/%%DIAGRAM%%(.*?)%%/s);
                            if (m) {
                                try { subDiagram = JSON.parse(m[1]); } catch { /* ignore */ }
                                subText = subText.replace(/%%DIAGRAM%%.*?%%/s, '').trim();
                            }
                        }

                        return {
                            ...subQuestion,
                            id: subQuestion.id || String.fromCharCode(97 + subIndex),
                            text: subText,
                            marks: Number(subQuestion.marks) || 1,
                            answer: subQuestion.answer || 'See marking scheme.',
                            explanation: subExplanation,
                            diagram: subDiagram,
                        };
                    }),
                };
            }),
        };
    }),
});

export async function generateExamPaper(params: GenerateExamPaperParams): Promise<ExamPaper> {
    // --- SAFETY LIMITS ---
    const MAX_TOTAL_QUESTIONS = 100;
    let totalQuestions = 0;
    for (const section of params.sections) {
        totalQuestions += section.numQuestions;
    }
    if (totalQuestions > MAX_TOTAL_QUESTIONS) {
        throw new Error(`Safety limit exceeded: You requested ${totalQuestions} questions. The maximum allowed per generation is ${MAX_TOTAL_QUESTIONS} to prevent excessive credit usage and timeouts.`);
    }
    // ---------------------

    const sectionsPrompt = params.sections.map(s => {
        const optionsInfo = (s.enableOptions || s.questionType === 'multiple-choice')
            ? ` Ensure all questions in this section are multiple-choice with exactly ${s.numOptions || 4} options. Set their 'type' field to 'multiple-choice'.`
            : '';
        return `- ${s.name}: ${s.numQuestions} questions of type '${s.questionType}', style '${s.questionStyle}', totaling ${s.marks} marks.${optionsInfo}`;
    }).join('\n');

    const rolePrompt = params.isLocalised
        ? "You are an expert Kenyan teacher setting a national exam (like KCPE or KCSE)."
        : "You are an expert educator setting a standard, globally applicable academic exam.";

    let documentPrompt = "";
    let documentConstraints = "";
    if (params.documentStyleAnalysis) {
        const { tone, questionPatterns, mathStyle, systemPromptSnapshot } = params.documentStyleAnalysis;
        documentPrompt = `\n\n=== LEARNED TEACHER STYLE TO REPLICATE ===
Tone: ${tone}
Question Patterns & Phrasing: ${questionPatterns}
Math / Notation Style: ${mathStyle}

EXPERT STYLE INSTRUCTIONS (CRITICAL):
${systemPromptSnapshot}
==========================================\n\n`;
        documentConstraints = `
CRITICAL FRESHNESS & STYLE CONSTRAINTS:
- You MUST generate a completely FRESH and ORIGINAL exam paper.
- DO NOT copy or repeat specific questions from the teacher's past papers.
- You MUST apply the 'LEARNED TEACHER STYLE TO REPLICATE' exactly. Your goal is to write a new paper that sounds like it was written by the same teacher.
- Adopt their specific phrasing, tone, difficulty, and mathematical notation style perfectly.
- USE LaTeX formatting for all mathematical formulas, equations, and symbols. ALWAYS wrap ALL math, numbers, and variables in single '$' for inline math (e.g., $f'(x) = 3x^2 \sin(x)$) and double '$$' for block math.
`;
    }

    const difficultyMap: Record<string, string> = {
        'Easy': 'Primary school level (children)',
        'Medium': 'High school level',
        'Hard': 'University / College level'
    };
    const mappedDifficulty = difficultyMap[params.difficulty] || params.difficulty;

    const stylePrompt = params.styleProfileSnapshot
        ? `\n\n=== STYLE TUNING APPLIED ===\n${params.styleProfileSnapshot}\n\nSTYLE ANALYSIS INSTRUCTIONS:\nBefore generating any question, internally derive the following style dimensions from the examples above:\n1. REGISTER — vocabulary level, formal vs semi-formal tone, sentence structure.\n2. DEPTH — surface recall / application / analysis / synthesis pattern.\n3. QUESTION VERBS — preferred starters (Calculate, Describe, Explain, etc.).\n4. FORMAT — presence of sub-questions, context passages, mark allocations shown in text.\n5. LENGTH — typical stem length, sub-part count.\n6. MATH NOTATION — LaTeX usage pattern; if examples use $...$ mirror it exactly.\n7. CULTURAL FLAVOUR — local names, units, example types.\n\nSTRICT CONSTRAINTS:\n- Do NOT deviate from the derived style under any circumstances.\n- Mirror even subtle phrasing from the examples.\n================================\n`
        : '';

    const prompt = `${rolePrompt}
Generate an exam paper for the subject: ${params.subject}.
Topics to cover: ${params.topics.join(', ')}.
Difficulty: ${params.difficulty} (Target Audience: ${mappedDifficulty}).
Additional Instructions: ${params.instructions}${documentPrompt}${stylePrompt}

SECTIONS REQUIRED:
${sectionsPrompt}

CRITICAL INSTRUCTIONS:${documentConstraints}
1. ${params.isLocalised ? "Make the questions distinctly Kenyan-themed where applicable (e.g., names, locations, currency). For university-level topics, use standard academic phrasing as seen in typical university exams." : "Make the questions standard, globally applicable, and professional. Do not use specific regional themes unless explicitly requested in the topics."}
2. Provide step-by-step explanations (marking scheme) for each question.
2b. Explanations must be concise: maximum 4 short steps per question and maximum 3 short steps per sub-question. NEVER include self-talk, uncertainty, prompt analysis, corrections, or phrases like "let me recheck", "I made an error", "the prompt says", or "I will correct this".
3. NEVER include question numbers (e.g., "1.", "2.") or sub-question letters (e.g., "a)", "b)", "Q2a") inside the 'text' or 'options' fields for both main questions and sub-questions. The UI handles numbering automatically.
4. DO NOT include the marks in the question 'text' itself (e.g., do not write "(5 marks)" at the end of the text). Use the 'marks' field in the JSON for this.
5. USE LaTeX formatting for all mathematical formulas, equations, and symbols. ALWAYS wrap ALL math, numbers, and variables in single '$' for inline math (e.g., $f'(x) = 3x^2 \sin(x)$) and double '$$' for block math. This applies to the 'text', 'options', 'answer', and 'explanation' fields. CRITICAL: Do NOT shy away from complex math symbols or equations if the topic involves math, physics, or chemistry. Generate them exactly as expected in a real exam.
6. Ensure the output is a valid JSON object matching the requested schema.
7. CRITICAL: Ensure the number of questions and total marks for each section EXACTLY matches the requirements. DO NOT generate extra questions. DO NOT loop or repeat questions.
8. If a section's style is 'Sub-questions', ALL questions MUST have sub-questions (where the main 'text' is ONLY the context/statement/passage and the actual questions are in the 'subQuestions' array).
9. If a section's style is 'Both (Mixed)', generate a mix: SOME questions MUST have sub-questions, and OTHER questions MUST NOT have sub-questions (just a single question asked). ENFORCE THIS MIX.
10. IMPORTANT: If a question has sub-questions, DO NOT provide 'options' or a real 'answer' for the main question (put "N/A" for answer/explanation). Instead, if it's a multiple-choice section, put the 'options' and 'answer' inside EACH sub-question.
11. If a question does NOT have sub-questions, the main 'text' is the question itself, and it should have 'options' (if multiple-choice) and an 'answer'.
12. ${params.includeDiagrams ? (() => {
    const countText = !params.diagramCount || params.diagramCount === 'random'
      ? 'a random number (minimum 2)'
      : `exactly ${params.diagramCount}`;
    const topicText = params.diagramTopics
      ? `ONLY on questions that cover these topics: ${params.diagramTopics}. Do not place diagrams on other topics.`
      : 'spread across different topics.';
    return `MANDATORY DIAGRAMS: Include ${countText} diagram(s) in this exam. Place them ${topicText}
    Each diagram question text MUST explicitly reference the diagram (e.g. "Study the graph below...", "Use the diagram below...").
    Every graph/area question MUST include all numeric data needed: function equation, xRange, yRange, shadeRange, verticalLines.
    
    IMPORTANT: Do NOT embed diagrams as %%DIAGRAM%% markers inside a JSON string — the JSON parser will break.
    Instead, set the question's "diagram" field to a valid JSON object with the diagram spec.
    
    Use ONLY these diagram types and structures in the "diagram" field:
    { "type": "right-triangle", "legs": [3, 4], "showSquares": false, "caption": "Right-angled triangle" }
    { "type": "triangle", "vertices": [{"x":0,"y":0,"label":"A"},{"x":6,"y":0,"label":"B"},{"x":3,"y":4,"label":"C"}], "caption": "Triangle ABC" }
    { "type": "circle", "center": {"x":0,"y":0}, "radius": 5, "caption": "Circle, radius 5" }
    { "type": "area-region", "equation": "y = x^2", "xRange": [0,4], "yRange": [0,18], "shadeRange": {"from":1,"to":3}, "verticalLines": [1,3], "caption": "Region under y = x^2" }
    { "type": "coordinate-plane", "points": [{"x":2,"y":3,"label":"A"}], "xRange": [-5,5], "yRange": [-5,5], "caption": "Coordinate plane" }
    { "type": "vector", "origin": {"x":0,"y":0}, "end": {"x":3,"y":4}, "caption": "Vector v" }
    { "type": "number-line", "min": -5, "max": 10, "shade": [[2,7]], "caption": "Number line" }
    { "type": "box-plot", "min": 2, "q1": 4, "median": 6, "q3": 8, "max": 10, "caption": "Box plot" }
    { "type": "bar-chart", "bars": [{"label":"A","value":5},{"label":"B","value":8}], "caption": "Bar chart" }
    { "type": "normal-distribution", "mean": 0, "stdDev": 1, "shadeRange": {"from":-1,"to":1}, "caption": "N(0,1)" }
    { "type": "unit-circle", "angle": 45, "showSin": true, "showCos": true, "caption": "Unit circle" }
    { "type": "bearing", "bearing": 60, "distance": 5, "fromPoint": {"x":0,"y":0,"label":"A"}, "toPoint": {"x":0,"y":0,"label":"B"}, "caption": "Bearing 060°" }
    { "type": "bearing-journey", "bearingJourney": {"origin":{"label":"P"},"legs":[{"bearing":70,"distance":15,"toLabel":"Q"}]}, "caption": "Journey" }
    { "type": "locus", "locusType": "from-point", "locusRadius": 3, "fromPoint": {"x":0,"y":0,"label":"A"}, "caption": "Locus" }
    { "type": "scale-drawing", "scale": 50, "scaleUnit": "m", "vertices": [{"x":60,"y":220,"label":"A"}], "caption": "Scale 1:50" }
    `;
})() : 'CRITICAL: DO NOT generate ANY diagrams. The "diagram" field MUST be null or omitted for all questions. Do not reference diagrams in the text.'}
13. You MAY use standard markdown formatting like bolding (**) or italics (*) where appropriate to emphasize text.
14. For questions with sub-questions, you MUST generate at least TWO sub-questions (never just one). Vary the number of sub-questions based on the total marks and complexity. For heavier mark questions, generate up to 4 or 5 sub-questions. Do NOT include sub-question text inside the main question's text field.
15. VARIETY IS MANDATORY: Every question must use a DIFFERENT opening verb. Never start two questions with the same word. Rotate through: Evaluate, Calculate, Find, Determine, Prove, Sketch, Solve, State and explain, Show that, Given that ... find, Express, Simplify, Factorise, Differentiate, Integrate. Spread questions evenly across ALL listed topics — do not cluster multiple questions on the same sub-topic.`;

    // Replaced diagramSchema with nothing since we aren't using the diagram field.

    const modelName = await getModelForFeature("independent_examGeneration");
    const model = getGenerativeModel(ai, { 
        model: modelName,
        generationConfig: {
            temperature: 1.2,      // High enough for variety; exam schema keeps it factually grounded
            maxOutputTokens: 32768,
            responseMimeType: "application/json",
            responseSchema: {
                type: "object",
                properties: {
                    title: { type: "string", description: "The title of the exam paper, e.g., MOCK EXAMINATION 2026" },
                    sections: {
                        type: "array",
                        items: {
                            type: "object",
                            properties: {
                                name: { type: "string", description: "Section name, e.g., SECTION A" },
                                instructions: { type: "string", description: "Instructions for this section" },
                                questions: {
                                    type: "array",
                                    items: {
                                        type: "object",
                                        properties: {
                                            id: { type: "string", description: "Unique question ID like Q1, Q2" },
                                            text: { type: "string", description: "The main question text" },
                                            type: { type: "string", description: "One of: multiple-choice, short-answer, essay" },
                                            marks: { type: "number", description: "Total marks for this question" },
                                            difficulty: { type: "string", description: "Easy, Medium, or Hard" },
                                            topic: { type: "string", description: "The specific topic this question covers" },
                                            image: { type: "string", description: "Optional image URL or data URI for a question diagram. Omit if not used." },
                                            options: {
                                                type: "array",
                                                items: { type: "string" },
                                                description: "Options if multiple choice. Omit if not."
                                            },
                                            answer: { type: "string", description: "The correct answer" },
                                            explanation: {
                                                type: "array",
                                                items: { type: "string" },
                                                description: "Step-by-step explanation or marking scheme. Each item is a step."
                                            },
                                            subQuestions: {
                                                type: "array",
                                                items: {
                                                    type: "object",
                                                    properties: {
                                                        id: { type: "string", description: "e.g., a, b, c" },
                                                        text: { type: "string" },
                                                        type: { type: "string", description: "One of: multiple-choice, short-answer, essay" },
                                                        image: { type: "string", description: "Optional image URL or data URI for a sub-question diagram. Omit if not used." },
                                                        options: {
                                                            type: "array",
                                                            items: { type: "string" },
                                                            description: "Options if multiple choice. Omit if not."
                                                        },
                                                        marks: { type: "number" },
                                                        answer: { type: "string" },
                                                        explanation: {
                                                            type: "array",
                                                            items: { type: "string" }
                                                        }
                                                    },
                                                    required: ["id", "text", "marks", "answer", "explanation"]
                                                },
                                                description: "Sub-questions if any. Omit if not."
                                            }
                                        },
                                        required: ["id", "text", "type", "marks", "difficulty", "answer", "explanation"]
                                    }
                                }
                            },
                            required: ["name", "instructions", "questions"]
                        }
                    }
                },
                required: ["title", "sections"]
            }
        }
    });

    const attempts = [
        prompt,
        `${prompt}

RETRY REQUIREMENT:
Your previous response could not be used. Regenerate the complete paper as VALID JSON ONLY.
Keep every explanation concise. Do not include self-correction, commentary, markdown fences, or any text outside the JSON object.`,
    ];

    for (const attemptPrompt of attempts) {
        try {
            const result = await model.generateContent(attemptPrompt);
            const response = await result.response;
            const text = response.text();
            if (!text) continue;
            return normalizeGeneratedPaper(parseExamPaperJson(text), params);
        } catch (error) {
            console.warn("[generateExamPaper] Model response was not usable; retrying or falling back.", error);
        }
    }

    console.warn("[generateExamPaper] Falling back to deterministic paper after unusable model responses.");
    return createFallbackExamPaper(params);
}

export async function regenerateQuestion(params: {
    originalQuestion: any;
    instructions: string;
    subject?: string;
}): Promise<any> {
    const diagramSchema = {
        type: "object" as const,
        properties: {
            type: { type: "string" as const },
            caption: { type: "string" as const },
            equation: { type: "string" as const },
            expression: { type: "string" as const },
            curve: { type: "string" as const },
            xRange: { type: "array" as const, items: { type: "number" as const } },
            yRange: { type: "array" as const, items: { type: "number" as const } },
            verticalLines: { type: "array" as const, items: { type: "number" as const } },
            shadeUnderCurve: { type: "boolean" as const },
            shadeRange: {
                type: "object" as const,
                properties: {
                    from: { type: "number" as const },
                    to: { type: "number" as const }
                }
            },
            points: {
                type: "array" as const,
                items: {
                    type: "object" as const,
                    properties: {
                        x: { type: "number" as const },
                        y: { type: "number" as const },
                        label: { type: "string" as const }
                    }
                }
            },
            line: {
                type: "object" as const,
                properties: {
                    x1: { type: "number" as const },
                    y1: { type: "number" as const },
                    x2: { type: "number" as const },
                    y2: { type: "number" as const },
                    label: { type: "string" as const }
                }
            }
        }
    };
    const modelName = await getModelForFeature("independent_examGeneration");
    const model = getGenerativeModel(ai, { 
        model: modelName,
        generationConfig: {
            maxOutputTokens: 8192,
            responseMimeType: "application/json",
            responseSchema: {
                type: "object",
                properties: {
                    id: { type: "string" },
                    text: { type: "string" },
                    type: { type: "string" },
                    marks: { type: "number" },
                    difficulty: { type: "string" },
                    topic: { type: "string" },
                    image: { type: "string" },
                    diagram: diagramSchema,
                    options: { type: "array", items: { type: "string" } },
                    answer: { type: "string" },
                    explanation: { type: "array", items: { type: "string" } },
                    subQuestions: {
                        type: "array",
                        items: {
                            type: "object",
                            properties: {
                                id: { type: "string" },
                                text: { type: "string" },
                                type: { type: "string" },
                                image: { type: "string" },
                                diagram: diagramSchema,
                                options: { type: "array", items: { type: "string" } },
                                marks: { type: "number" },
                                answer: { type: "string" },
                                explanation: { type: "array", items: { type: "string" } }
                            },
                            required: ["id", "text", "marks", "answer", "explanation"]
                        }
                    }
                },
                required: ["id", "text", "type", "marks", "difficulty", "answer", "explanation"]
            }
        }
    });

    const prompt = `You are an expert educator.
Here is an existing exam question:
${JSON.stringify(params.originalQuestion, null, 2)}

The teacher wants to modify or regenerate this question.
INSTRUCTIONS FROM TEACHER: "${params.instructions}"

If the instructions say "Regenerate" or "Generate new", create a completely new question of similar difficulty, topic, marks, and type.
If the instructions give specific changes (e.g., "Make it harder", "Use a different example"), adjust the question accordingly while maintaining its structure.
CRITICAL: Output the new question as a valid JSON object matching the exact schema provided.
USE LaTeX formatting for all mathematical formulas, wrapping inline math in single '$'.
If the question uses a diagram, make the wording depend on it ("Study the graph below...", "Use the figure below...") and include enough diagram data to render the graph/figure. Never return an empty coordinate-plane diagram.`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    let text = response.text();
    if (!text) throw new Error("Failed to regenerate question.");

    try {
        text = text.replace(/^```json\n/, '').replace(/\n```$/, '');
        return JSON.parse(text);
    } catch (e) {
        throw new Error("Failed to parse regenerated question JSON.");
    }
}
